import React, { useState, useEffect } from 'react';
const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = document.querySelectorAll('[data-section]');
      let current = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.pageYOffset >= sectionTop - 200) {
          current = section.getAttribute('data-section');
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const tableOfContents = [
    { id: 'overview', title: 'Overview' },
    { id: 'eligibility', title: 'Eligibility' },
    { id: 'account', title: 'Account Responsibilities' },
    { id: 'services', title: 'Our Services' },
    { id: 'acceptable-use', title: 'Acceptable Use' },
    { id: 'payments', title: 'Payments & Subscriptions' },
    { id: 'ip', title: 'Intellectual Property' },
    { id: 'disclaimers', title: 'Disclaimers' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'termination', title: 'Termination' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <div className="privacy-policy">
      {/* Header */}
      <header className={`policy-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="breadcrumb">
              <span>Legal</span>
              <span className="separator">/</span>
              <span>Terms of Service</span>
            </div>
            <h1>Terms of Service</h1>
            <p className="header-description">
              Please read these terms carefully before using NyLeads AI
            </p>
            <div className="last-updated">
              <span className="update-badge">Last updated</span>
              <time dateTime="2026-01-28">January 28, 2025</time>
            </div>
          </div>
        </div>
      </header>

      <div className="policy-container">
        <div className="container">
          <div className="policy-layout">
            {/* Table of Contents */}
            <aside className="toc-sidebar">
              <div className="toc-sticky">
                <h3>Contents</h3>
                <nav className="toc-nav">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      className={`toc-link ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => scrollToSection(item.id)}
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="policy-content">
              {/* Overview */}
              <section data-section="overview" className="policy-section">
                <h2>Overview</h2>
                <p className="lead">
                  These Terms of Service ("Terms") govern your access to and use of NyLeads AI's
                  platform, which provides AI-powered restaurant lead generation and business
                  intelligence services across New York City. By accessing or using our services,
                  you agree to be bound by these Terms.
                </p>
                <div className="info-card">
                  <div className="info-card-header">
                    <h4>Key Points</h4>
                  </div>
                  <ul>
                    <li>You must be 18 or older and acting on behalf of a legitimate business to use NyLeads AI</li>
                    <li>Leads provided are for lawful B2B sales and outreach purposes only</li>
                    <li>You are responsible for how you use the lead data we provide</li>
                    <li>Misuse of our platform or data may result in immediate account termination</li>
                  </ul>
                </div>
              </section>

              {/* Eligibility */}
              <section data-section="eligibility" className="policy-section">
                <h2>Eligibility</h2>
                <p>
                  To use NyLeads AI, you must meet the following requirements:
                </p>
                <div className="data-grid">
                  <div className="data-item">
                    <h4>Age Requirement</h4>
                    <ul>
                      <li>Must be at least 18 years old</li>
                      <li>Legally capable of entering contracts</li>
                      <li>Acting on your own behalf or for a business</li>
                    </ul>
                  </div>
                  <div className="data-item">
                    <h4>Business Use</h4>
                    <ul>
                      <li>Must represent a legitimate business entity</li>
                      <li>Use leads only for lawful B2B outreach</li>
                      <li>Comply with all applicable sales and marketing laws</li>
                    </ul>
                  </div>
                  <div className="data-item">
                    <h4>Geographic Scope</h4>
                    <ul>
                      <li>Services are intended for users operating in or targeting NYC</li>
                      <li>Must comply with local, state, and federal laws</li>
                      <li>International users subject to additional terms</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Account Responsibilities */}
              <section data-section="account" className="policy-section">
                <h2>Account Responsibilities</h2>
                <p>
                  When you create an account with NyLeads AI, you agree to:
                </p>
                <div className="usage-grid">
                  <div className="usage-card primary">
                    <div className="usage-icon">🔑</div>
                    <h4>Keep Credentials Secure</h4>
                    <p>Maintain the confidentiality of your login credentials and never share account access</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">✅</div>
                    <h4>Provide Accurate Info</h4>
                    <p>Supply truthful, current, and complete information when registering and using the platform</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">🔔</div>
                    <h4>Report Unauthorized Access</h4>
                    <p>Notify us immediately at security@nyleads.ai if you suspect any unauthorized use of your account</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">👤</div>
                    <h4>One Account Per User</h4>
                    <p>Each user may maintain only one active account; duplicate accounts may be suspended</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">📋</div>
                    <h4>Accept Responsibility</h4>
                    <p>You are responsible for all activity that occurs under your account</p>
                  </div>
                  <div className="usage-card">
                    <div className="usage-icon">🔄</div>
                    <h4>Keep Info Updated</h4>
                    <p>Update your account details promptly if any information changes</p>
                  </div>
                </div>
              </section>

              {/* Our Services */}
              <section data-section="services" className="policy-section">
                <h2>Our Services</h2>
                <p>
                  NyLeads AI provides AI-powered restaurant lead data and related tools to help
                  businesses identify, evaluate, and connect with NYC restaurants. Here's how our
                  service delivery works:
                </p>

                <div className="ai-process-flow">
                  <div className="process-step">
                    <div className="step-number">1</div>
                    <h4>Lead Discovery</h4>
                    <p>We surface verified NYC restaurant leads based on your filters and ideal customer profile</p>
                  </div>
                  <div className="process-arrow">→</div>
                  <div className="process-step">
                    <div className="step-number">2</div>
                    <h4>Data Enrichment</h4>
                    <p>Each lead is enriched with contact details, cuisine type, neighborhood, and business attributes</p>
                  </div>
                  <div className="process-arrow">→</div>
                  <div className="process-step">
                    <div className="step-number">3</div>
                    <h4>Export & Outreach</h4>
                    <p>Download or integrate your leads directly into your CRM or outreach workflow</p>
                  </div>
                </div>

                <div className="security-notice">
                  <h4>📌 Service Limitations</h4>
                  <ul>
                    <li>Lead data accuracy is subject to regular updates but cannot be guaranteed 100% at all times</li>
                    <li>We reserve the right to modify, suspend, or discontinue features with reasonable notice</li>
                    <li>Access to certain features depends on your active subscription plan</li>
                    <li>API access, if available, is subject to rate limits defined in your plan</li>
                  </ul>
                </div>
              </section>

              {/* Acceptable Use */}
              <section data-section="acceptable-use" className="policy-section">
                <h2>Acceptable Use</h2>
                <p>
                  You agree to use NyLeads AI only for lawful purposes. The following uses are expressly prohibited:
                </p>

                <div className="sharing-grid">
                  <div className="sharing-card allowed">
                    <h4>✅ Permitted Uses</h4>
                    <p>You may use our platform and lead data to:</p>
                    <ul>
                      <li>Conduct legitimate B2B sales outreach to NYC restaurants</li>
                      <li>Research market opportunities and competitor landscapes</li>
                      <li>Build targeted prospect lists for your sales team</li>
                      <li>Enrich existing CRM records with verified data</li>
                    </ul>
                  </div>
                  <div className="sharing-card restricted">
                    <h4>🚫 Prohibited Uses</h4>
                    <p>You may not use our platform or data to:</p>
                    <ul>
                      <li>Resell, sublicense, or redistribute lead data to third parties</li>
                      <li>Conduct spam, harassment, or unsolicited mass messaging</li>
                      <li>Scrape, clone, or reverse-engineer our platform</li>
                      <li>Violate any applicable privacy or anti-spam laws</li>
                    </ul>
                  </div>
                </div>

                <div className="no-sharing-notice">
                  <h4>⚠️ Enforcement</h4>
                  <ul>
                    <li>Violations may result in immediate account suspension or termination</li>
                    <li>We reserve the right to investigate suspected misuse at any time</li>
                    <li>We may cooperate with law enforcement for serious violations</li>
                    <li>No refunds will be issued for accounts terminated due to policy violations</li>
                  </ul>
                </div>
              </section>

              {/* Payments & Subscriptions */}
              <section data-section="payments" className="policy-section">
                <h2>Payments & Subscriptions</h2>
                <p>
                  NyLeads AI operates on a subscription basis. By selecting a paid plan, you agree to the following:
                </p>

                <div className="security-measures">
                  <div className="security-category">
                    <h4>💳 Billing</h4>
                    <ul>
                      <li>Subscriptions are billed monthly or annually in advance</li>
                      <li>All prices are in USD unless otherwise stated</li>
                      <li>Taxes may apply based on your location</li>
                    </ul>
                  </div>
                  <div className="security-category">
                    <h4>🔄 Renewals</h4>
                    <ul>
                      <li>Plans auto-renew unless cancelled before the renewal date</li>
                      <li>You'll receive a reminder email before each renewal</li>
                      <li>Cancel anytime from your account dashboard</li>
                    </ul>
                  </div>
                  <div className="security-category">
                    <h4>💰 Refunds</h4>
                    <ul>
                      <li>30-day money-back guarantee on first-time purchases</li>
                      <li>No refunds for partial months or unused lead credits</li>
                      <li>Contact support@nyleads.ai to request a refund</li>
                    </ul>
                  </div>
                  <div className="security-category">
                    <h4>📈 Plan Changes</h4>
                    <ul>
                      <li>Upgrades take effect immediately with prorated billing</li>
                      <li>Downgrades take effect at the next billing cycle</li>
                      <li>Lead limits reset at the start of each billing period</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Intellectual Property */}
              <section data-section="ip" className="policy-section">
                <h2>Intellectual Property</h2>
                <p>
                  All content, technology, and data on the NyLeads AI platform are protected by applicable
                  intellectual property laws. Here's what you need to know:
                </p>

                <div className="retention-timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker active"></div>
                    <h4>Our Platform</h4>
                    <p>The NyLeads AI platform, including its design, algorithms, and software, is owned exclusively by us</p>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <h4>Lead Data</h4>
                    <p>Lead data delivered through our platform is licensed to you for use within the scope of your plan — not sold or transferred</p>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <h4>Your Content</h4>
                    <p>You retain ownership of any content or data you upload; you grant us a limited license to use it to deliver our services</p>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <h4>Trademarks</h4>
                    <p>NyLeads AI and associated logos are our trademarks and may not be used without written permission</p>
                  </div>
                </div>
              </section>

              {/* Disclaimers */}
              <section data-section="disclaimers" className="policy-section">
                <h2>Disclaimers</h2>
                <p>
                  NyLeads AI provides its services on an "as is" and "as available" basis. We make no warranties
                  regarding the completeness or accuracy of lead data, and disclaim all liability to the fullest
                  extent permitted by law.
                </p>

                <div className="transfer-safeguards">
                  <div className="safeguard-item">
                    <h4>📋 Data Accuracy</h4>
                    <p>While we strive to keep restaurant data current, we cannot guarantee that all contact details or business information are accurate or up to date at the time of access</p>
                  </div>
                  <div className="safeguard-item">
                    <h4>⚡ Service Availability</h4>
                    <p>We do not guarantee uninterrupted access to the platform and are not liable for downtime caused by maintenance, outages, or circumstances beyond our control</p>
                  </div>
                  <div className="safeguard-item">
                    <h4>📈 Business Outcomes</h4>
                    <p>We make no guarantees regarding the results of your outreach efforts or the conversion rates of any leads provided through our platform</p>
                  </div>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section data-section="liability" className="policy-section">
                <h2>Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by applicable law, NyLeads AI shall not be liable for
                  any indirect, incidental, special, consequential, or punitive damages arising from your
                  use of our services, including but not limited to:
                </p>

                <div className="rights-grid">
                  <div className="right-card">
                    <div className="right-icon">💼</div>
                    <h4>Lost Business</h4>
                    <p>Loss of revenue, profits, or business opportunities resulting from use of our lead data</p>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">📉</div>
                    <h4>Data Inaccuracy</h4>
                    <p>Losses arising from inaccurate, outdated, or incomplete restaurant information</p>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">🔌</div>
                    <h4>Service Interruption</h4>
                    <p>Disruptions to your operations caused by platform downtime or technical issues</p>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">🔓</div>
                    <h4>Unauthorized Access</h4>
                    <p>Losses resulting from unauthorized access to your account due to your failure to secure credentials</p>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">⚖️</div>
                    <h4>Third-Party Actions</h4>
                    <p>Damages arising from actions or inactions of restaurants or other third parties in our database</p>
                  </div>
                  <div className="right-card">
                    <div className="right-icon">🛡️</div>
                    <h4>Liability Cap</h4>
                    <p>Our total liability is limited to the amount you paid us in the 3 months preceding the claim</p>
                  </div>
                </div>

                <div className="rights-notice">
                  <p>
                    <strong>Note:</strong> Some jurisdictions do not allow the exclusion of certain warranties
                    or limitation of liability for consequential damages. In such cases, our liability is
                    limited to the fullest extent permitted by applicable law.
                  </p>
                </div>
              </section>

              {/* Termination */}
              <section data-section="termination" className="policy-section">
                <h2>Termination</h2>
                <p>
                  Either party may terminate the relationship under these Terms. Here's how termination works:
                </p>

                <div className="notification-methods">
                  <div className="notification-item">
                    <div className="notification-icon">👤</div>
                    <h4>By You</h4>
                    <p>Cancel your subscription at any time from your account dashboard; access continues until the end of your billing period</p>
                  </div>
                  <div className="notification-item">
                    <div className="notification-icon">🏢</div>
                    <h4>By Us</h4>
                    <p>We may suspend or terminate your account for violations of these Terms, non-payment, or fraudulent activity, with or without notice</p>
                  </div>
                  <div className="notification-item">
                    <div className="notification-icon">📦</div>
                    <h4>Effect of Termination</h4>
                    <p>Upon termination, your license to use lead data ends; you must cease use of any exported data that conflicts with these Terms</p>
                  </div>
                </div>

                <div className="update-notice">
                  <p>
                    <strong>Data After Termination:</strong> Following account closure, we will retain your
                    personal data per our Privacy Policy. You may request deletion by contacting
                    privacy@nyleads.ai within 30 days of termination.
                  </p>
                </div>
              </section>

              {/* Governing Law */}
              <section data-section="governing-law" className="policy-section">
                <h2>Governing Law</h2>
                <p>
                  These Terms are governed by the laws of the State of New York, without regard to
                  its conflict of law provisions. Any disputes arising from these Terms shall be
                  resolved as follows:
                </p>

                <div className="transfer-safeguards">
                  <div className="safeguard-item">
                    <h4>🏛️ Jurisdiction</h4>
                    <p>Any legal action shall be brought exclusively in the state or federal courts located in New York County, New York</p>
                  </div>
                  <div className="safeguard-item">
                    <h4>🤝 Dispute Resolution</h4>
                    <p>We encourage resolving disputes informally first — contact us at legal@nyleads.ai before initiating any formal proceedings</p>
                  </div>
                  <div className="safeguard-item">
                    <h4>📋 Severability</h4>
                    <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect</p>
                  </div>
                </div>
              </section>

              {/* Contact */}
              <section data-section="contact" className="policy-section">
                <h2>Contact Us</h2>
                <p>
                  If you have questions about these Terms of Service or need legal clarification,
                  we're here to help:
                </p>

                <div className="contact-grid">
                  <div className="contact-card primary">
                    <h4>⚖️ Legal Team</h4>
                    <p><strong>legal@nyleads.ai</strong></p>
                    <p>For Terms of Service questions and legal matters</p>
                    <p className="response-time">Response within 48 hours</p>
                  </div>
                  <div className="contact-card">
                    <h4>💬 General Support</h4>
                    <p><strong>support@nyleads.ai</strong></p>
                    <p>For account and billing inquiries</p>
                    <p className="response-time">Response within 24 hours</p>
                  </div>
                  <div className="contact-card">
                    <h4>📍 Mailing Address</h4>
                    <p><strong>NyLeads AI Legal Department</strong><br/>
                    [Your Business Address]<br/>
                    New York, NY [ZIP Code]<br/>
                    United States</p>
                  </div>
                  <div className="contact-card">
                    <h4>🏛️ Data Protection Officer</h4>
                    <p><strong>dpo@nyleads.ai</strong></p>
                    <p>For EU/UK data protection matters</p>
                    <p className="response-time">Response within 72 hours</p>
                  </div>
                </div>

                <div className="emergency-contact">
                  <h4>🚨 Legal Notices</h4>
                  <p>
                    For formal legal notices or dispute communications, please send written correspondence to{' '}
                    <strong>legal@nyleads.ai</strong> or our mailing address above. Electronic notices
                    are accepted unless otherwise required by law.
                  </p>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      {/* Footer */}

    </div>
  );
};

export default TermsOfService;